/**
 * TraceDetail - Shows full trace input/output and metadata
 */

import { useState } from 'react';
import { X, Clock, Hash, Coins, Tag, AlertCircle, User, MessageSquare } from 'lucide-react';
import { useTraceDetail } from '../hooks/useTraces';
import { SpanViewer } from './SpanViewer';
import { SmartContent, JsonBlock } from './SmartContent';
import { useI18n } from '@/i18n';

interface TraceDetailProps {
  traceId: string;
  onClose: () => void;
}

export function TraceDetail({ traceId, onClose }: TraceDetailProps) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const { data: trace, isLoading, isError } = useTraceDetail(traceId);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        {t.loading}
      </div>
    );
  }

  if (isError || !trace) {
    return (
      <div className="h-full flex items-center justify-center text-red-500">
        {t.loadFailed}
      </div>
    );
  }

  const durationMs = trace.duration;
  const totalTokens = trace.usage?.total_tokens;
  const promptTokens = trace.usage?.prompt_tokens;
  const completionTokens = trace.usage?.completion_tokens;

  // Extract user_id and topic_id from input (OTEL root span) or metadata (LiteLLM)
  const input = trace.input as Record<string, any> | null | undefined;
  const meta = trace.metadata as Record<string, any> | null | undefined;
  const userId = input?.['chat.user_id'] as string | undefined
    || meta?.chat?.user_id as string | undefined
    || meta?.user as string | undefined;
  const topicId = input?.['chat.topic_id'] as string | undefined
    || meta?.chat?.topic_id as string | undefined
    || trace.thread_id as string | undefined;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {trace.name || trace.id.slice(0, 12)}
          </h3>
          <p className="text-xs text-gray-500 font-mono mt-0.5">{trace.id}</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Stats bar */}
      <div className="flex gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs">
        {durationMs != null && (
          <span className="flex items-center gap-1 text-gray-600">
            <Clock className="w-3 h-3" />
            {durationMs >= 1000 ? `${(durationMs / 1000).toFixed(2)}s` : `${Math.round(durationMs)}ms`}
          </span>
        )}
        {totalTokens != null && (
          <span className="flex items-center gap-1 text-gray-600">
            <Hash className="w-3 h-3" />
            {totalTokens.toLocaleString()} {t.tokens}
            {promptTokens != null && completionTokens != null && (
              <span className="text-gray-400">
                ({promptTokens.toLocaleString()} / {completionTokens.toLocaleString()})
              </span>
            )}
          </span>
        )}
        {trace.total_estimated_cost != null && (
          <span className="flex items-center gap-1 text-gray-600">
            <Coins className="w-3 h-3" />
            ${trace.total_estimated_cost.toFixed(4)}
          </span>
        )}
        {trace.tags && trace.tags.length > 0 && (
          <span className="flex items-center gap-1 text-gray-600">
            <Tag className="w-3 h-3" />
            {trace.tags.join(', ')}
          </span>
        )}
      </div>

      {/* User/Topic context bar */}
      {(userId || topicId) && (
        <div className="flex gap-4 px-4 py-1.5 border-b border-gray-100 bg-amber-50/50 text-xs">
          {userId && (
            <span className="flex items-center gap-1 text-amber-800 font-mono" title={userId}>
              <User className="w-3 h-3" />
              {userId}
            </span>
          )}
          {topicId && (
            <span className="flex items-center gap-1 text-green-800 font-mono" title={topicId}>
              <MessageSquare className="w-3 h-3" />
              {topicId}
            </span>
          )}
        </div>
      )}

      {/* Error banner */}
      {trace.error_info && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">{(trace.error_info as Record<string, string>).exception_type || t.errorLabel}</p>
            <p className="mt-0.5">{(trace.error_info as Record<string, string>).message || JSON.stringify(trace.error_info)}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {/* Input */}
        <CollapsibleSection title={t.sectionInput} defaultOpen>
          <SmartContent data={trace.input} />
        </CollapsibleSection>

        {/* Output */}
        <CollapsibleSection title={t.sectionOutput} defaultOpen>
          <SmartContent data={trace.output} />
        </CollapsibleSection>

        {/* Span Tree */}
        <CollapsibleSection title={t.sectionSpans.replace('{count}', String(trace.span_count ?? '?'))} defaultOpen>
          <SpanViewer traceId={traceId} traceStartTime={trace.start_time} traceDuration={trace.duration ?? null} />
        </CollapsibleSection>

        {/* Metadata */}
        {trace.metadata != null && (
          <CollapsibleSection title={t.sectionMetadata}>
            <JsonBlock data={trace.metadata} />
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}


function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span>{title}</span>
        <span className="text-gray-400">{open ? '\u2212' : '+'}</span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}
